import { Select, Space } from "antd";
import { useTranslation } from "react-i18next";
import { useRefreshState } from "../context/RefreshContext.tsx";
import { LocaleNumberInput } from "./LocaleNumberInput.tsx";
import { css } from "@linaria/core";

const quantityInput = css`
  width: 5rem;
`;
const unitSelect = css`
  width: 4rem;
`;

type MeasurementUnit = { readonly id: string; readonly code: string; readonly nameEn: string; readonly nameBg: string };

export type QuantityUnitValue = {
  readonly quantity: number | null;
  readonly unitId: string;
};

// value/onChange follow the antd Form contract (single value, single-argument
// callback), so the component can be bound via <Form.Item name="...">.
// Commits on blur / unit change, not on every keystroke.
type QuantityUnitInputProps = {
  readonly value?: QuantityUnitValue | undefined;
  readonly onChange?: ((value: QuantityUnitValue) => void) | undefined;
  readonly units: readonly MeasurementUnit[];
  readonly inputClassName?: string | undefined;
  readonly onPressEnter?: (() => void) | undefined;
};

export const QuantityUnitInput = ({
  value = undefined,
  onChange = undefined,
  units,
  inputClassName = undefined,
  onPressEnter = undefined,
}: QuantityUnitInputProps) => {
  const { i18n } = useTranslation();
  const unitName = (u: MeasurementUnit) => (i18n.language === "bg" ? u.nameBg : u.nameEn);

  const [currentQuantity, setCurrentQuantity] = useRefreshState(value?.quantity ?? null);
  const [selectedUnitId, setSelectedUnitId] = useRefreshState(value?.unitId ?? "");

  const unitOptions = units.map((u) => ({ value: u.id, label: u.code, title: unitName(u) }));

  const handleCommit = (quantity: number | null) => {
    setCurrentQuantity(quantity);
    onChange?.({ quantity, unitId: selectedUnitId });
  };

  const handleUnitChange = (unitId: string) => {
    setSelectedUnitId(unitId);
    onChange?.({ quantity: currentQuantity, unitId });
  };

  return (
    <Space.Compact>
      <LocaleNumberInput
        className={quantityInput}
        classNames={inputClassName ? { input: inputClassName } : undefined}
        defaultValue={value?.quantity ?? undefined}
        onCommit={handleCommit}
        onPressEnter={onPressEnter}
      />
      <Select
        className={unitSelect}
        onChange={handleUnitChange}
        options={unitOptions}
        popupMatchSelectWidth={false}
        showSearch={{ optionFilterProp: "label", autoClearSearchValue: true }}
        value={selectedUnitId === "" ? undefined : selectedUnitId}
      />
    </Space.Compact>
  );
};
